import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import GpoFilter, { matchesGpo, FilterCount } from './GpoFilter';
import { printDocument, printBtnStyle } from '../utils/print';
import { ListFooter } from '../ui';

// Portal palette.
const NAVY = '#2D2C72', INK = '#3B3A8F', RED = '#E22D2D', WARM = '#F7F6F3', VIOLET = '#EEEDF8';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const APPROVER_ROLES = ['admin', 'management'];

// Status → label + colours.
const STATUS_META = {
  draft:            { label: 'Draft',            bg: '#ececed', fg: '#555' },
  pending_approval: { label: 'Pending Approval', bg: '#FAEEDA', fg: '#854F0B' },
  approved:         { label: 'Approved',         bg: '#DDF3E4', fg: '#0F7B3F' },
  held:             { label: 'Held',             bg: '#FBE2E2', fg: RED },
};
const DECISION_LABEL = {
  proceed: 'Proceed',
  proceed_with_conditions: 'Proceed with Conditions',
  hold: 'Hold',
};
// Rating / option → colour (green = good, amber = caution, red = risk).
const TONE = { good: '#0F7B3F', warn: '#854F0B', bad: RED };
const ratingTone = (r) => ({ low: 'good', med: 'warn', high: 'bad' }[String(r || '').toLowerCase()]);
const optionTone = (v) => {
  const s = String(v || '').toLowerCase();
  if (['ok', 'available', 'comfortable'].includes(s)) return 'good';
  if (s === 'tight') return 'warn';
  if (['at-risk', 'no'].includes(s)) return 'bad';
  return null;
};

const RATINGS = ['low', 'med', 'high'];
const RATING_LABEL = { low: 'Low', med: 'Medium', high: 'High' };

// Auto-calc (mirrors the backend): overall = highest-wins of the section ratings.
const RANK = { low: 1, med: 2, high: 3 };
const computeOverall = (ratings) => {
  let m = 0;
  ratings.forEach((r) => { const v = RANK[String(r || '').toLowerCase()]; if (v > m) m = v; });
  return m ? ['', 'low', 'med', 'high'][m] : '';
};
const SUGGESTED_DECISION = { low: 'proceed', med: 'proceed_with_conditions', high: 'hold' };
const isFlagged = (r) => ['med', 'high'].includes(String(r || '').toLowerCase());

// Sign-off categories. Fabric, Quality and Production are MANDATORY signatories on every
// RA (required:true) — even Low is a "reviewed, no concern" sign-off. Commercial and Trims
// are optional (owned only when flagged Med/High). `roles` locks each category's owner
// picker to that department (Q5); `ratingKey` is its (now split) rating column.
const OWNER_CATS = [
  { key: 'commercial',      ratingKey: 'commercial_rating',      label: 'Commercial',          dept: 'Commercial / Accounts', roles: ['accounts'] },
  { key: 'material_fabric', ratingKey: 'material_fabric_rating', label: 'Material — Fabric',   dept: 'Fabric',                roles: ['deroi_operator'],                    required: true },
  { key: 'material_trims',  ratingKey: 'material_trims_rating',  label: 'Material — Trims',    dept: 'Trims',                 roles: ['deroi_operator'] },
  { key: 'capacity',        ratingKey: 'capacity_rating',        label: 'Capacity / Delivery', dept: 'Production / PPC',      roles: ['production_incharge', 'ppc_manager'], required: true },
  { key: 'technical',       ratingKey: 'technical_rating',       label: 'Technical / Quality', dept: 'Quality',               roles: ['quality_incharge'],                  required: true },
];
const CAT_BY_KEY = OWNER_CATS.reduce((m, c) => { m[c.key] = c; return m; }, {});
const CAT_LABEL = OWNER_CATS.reduce((m, c) => { m[c.key] = c.label; return m; }, {});

// ─── small presentational helpers ────────────────────────────────────────────
const labelStyle = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 };
const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const sectionTitleStyle = { fontSize: 13, fontWeight: 700, color: INK, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px', paddingBottom: 6, borderBottom: `2px solid ${VIOLET}` };
const cardStyle = { background: '#fff', border: '1px solid #ececec', borderRadius: 10, padding: 18, marginBottom: 16 };

function Badge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span style={{ background: m.bg, color: m.fg, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{m.label}</span>;
}

function RatingPill({ value }) {
  if (!value) return <span style={{ color: '#bbb' }}>—</span>;
  const tone = ratingTone(value);
  const c = TONE[tone] || '#555';
  return <span style={{ color: c, fontWeight: 700 }}>{RATING_LABEL[value] || value}</span>;
}

// Y/N toggle + note row.
function YesNo({ value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {['Y', 'N'].map((v) => {
        const on = value === v;
        const good = v === 'Y';
        return (
          <button key={v} type="button" disabled={disabled}
            onClick={() => onChange(v)}
            style={{ flex: 1, padding: '6px 0', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
              border: on ? 'none' : '1px solid #ddd',
              background: on ? (good ? '#DDF3E4' : '#FBE2E2') : '#fff',
              color: on ? (good ? TONE.good : TONE.bad) : '#999' }}>
            {v === 'Y' ? 'Yes' : 'No'}
          </button>
        );
      })}
    </div>
  );
}

// Pick-one chips (e.g. OK / Tight / At-Risk).
function ChipSelect({ options, value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const on = value === o;
        const tone = optionTone(o);
        const c = tone ? TONE[tone] : INK;
        return (
          <button key={o} type="button" disabled={disabled}
            onClick={() => onChange(o)}
            style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
              border: on ? `1px solid ${c}` : '1px solid #ddd',
              background: on ? (tone ? c + '1A' : VIOLET) : '#fff',
              color: on ? c : '#999' }}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

// Section rating selector (Low / Med / High).
function RatingSelect({ value, onChange, disabled }) {
  return (
    <div>
      <label style={labelStyle}>Section Rating</label>
      <div style={{ display: 'flex', gap: 6 }}>
        {RATINGS.map((r) => {
          const on = value === r;
          const c = TONE[ratingTone(r)];
          return (
            <button key={r} type="button" disabled={disabled}
              onClick={() => onChange(r)}
              style={{ flex: 1, padding: '7px 0', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
                border: on ? `1px solid ${c}` : '1px solid #ddd',
                background: on ? c + '1A' : '#fff', color: on ? c : '#999' }}>
              {RATING_LABEL[r]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label style={labelStyle}>{label}</label>{children}</div>
);

// Owner picker shown when a section is flagged Medium/High. Lists active users;
// stores the chosen user id. `dept` is the expected responsible department.
// `required` mandatory-roster pickers are always shown (even Low). `users` is already
// dept-filtered by the caller (Q5). A required picker with no dept users warns instead.
function OwnerPicker({ label, dept, users, value, onChange, disabled, required }) {
  const bg = required ? '#EEF4FF' : '#FFF8E8';
  const bd = required ? '#C9DBFF' : '#F3E2B8';
  const fg = required ? '#1E3A8A' : '#854F0B';
  return (
    <div style={{ marginTop: 12, background: bg, border: `1px solid ${bd}`, borderRadius: 8, padding: '10px 12px' }}>
      <label style={{ ...labelStyle, color: fg, marginBottom: 6 }}>
        {required ? '✔ ' : '⚠ '}{label} — {required ? 'mandatory sign-off' : 'flagged'}, assign owner
        <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> ({dept})</span>
      </label>
      <select value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, maxWidth: 360, background: disabled ? '#f5f5f5' : '#fff' }}>
        <option value="">Select {dept} head…</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}
      </select>
      {!users.length && <div style={{ fontSize: 11, color: '#B45309', marginTop: 4 }}>No active {dept} user available — a {dept} sign-off owner must exist.</div>}
    </div>
  );
}

const emptyForm = () => ({
  commercial: {}, commercial_rating: '',
  material: {}, material_fabric_rating: '', material_trims_rating: '',
  capacity: {}, capacity_rating: '',
  technical: {}, technical_rating: '',
  conditions: '',
  owners: {},   // { category: owner_user_id } for each sign-off category
});

export default function RiskAnalysis({ user, gpos = [], stage = 'order_execution', openGpoNo = null, onOpened }) {
  const isApprover = APPROVER_ROLES.includes(user?.role);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);            // { estimate, analysis, snapshot } when editing/reviewing
  const [form, setForm] = useState(emptyForm());
  const [decisionForm, setDecisionForm] = useState({ overall_rating: '', decision: '', conditions: '', rating_override_note: '', decision_override_note: '' });
  // SEEDED FROM THE SUGGESTION, not left blank. decide() refuses at the guard below when
  // overall_rating is empty — and because that guard uses alert(), a browser with dialogs
  // suppressed swallows the message AND still returns, so the Authorize button goes silent
  // and looks broken. An approver who accepts the system's suggested rating should not have
  // to re-pick it to make the button work. Changing it away from the suggestion still
  // demands an override note, exactly as before.
  useEffect(() => {
    const a = open?.analysis;
    if (!a) return;
    setDecisionForm((p) => (p.overall_rating ? p : { ...p, overall_rating: a.overall_rating_auto || '' }));
  }, [open?.analysis?.id, open?.analysis?.overall_rating_auto]);
  const [saving, setSaving] = useState(false);
  const [pickGpo, setPickGpo] = useState('');
  const [gpoQ, setGpoQ] = useState('');   // GET /risk (list) is UNCAPPED — client-side is safe
  const [items, setItems] = useState([]);            // risk_items for the open analysis
  const [owners, setOwners] = useState([]);          // selectable users for owner pickers

  const fetchList = useCallback(() => {
    api.get('/risk', { params: { stage } }).then((r) => setList(r.data.data || [])).catch(() => {});
  }, [stage]);
  useEffect(() => { fetchList(); }, [fetchList]);
  // Selectable owners (active users) for the per-flagged-risk pickers.
  useEffect(() => { api.get('/risk/owners').then((r) => setOwners(r.data.data || [])).catch(() => {}); }, []);

  // GPOs that don't yet have a Risk Analysis (for the "Start" picker). Re-keyed to
  // gpo_no: the RA is filled on a (draft) GPO; one RA per GPO.
  const withRA = new Set(list.map((r) => r.gpo_no));
  // Style and buyer ride along with the GPO: on this tab people quote a style as often as
  // a GPO number, and both are already columns in the table below.
  const shown = list.filter((r) => matchesGpo(gpoQ, r.gpo_no, r.estimate_no, r.style_no, r.buyer, r.brand));
  const startable = gpos.filter((g) => !withRA.has(g.gpo_no));

  const loadAnalysis = async (gpoNo) => {
    const r = await api.get('/risk/by-gpo/' + gpoNo, { params: { stage } });
    const { analysis, snapshot, items: its } = r.data.data;
    const gpo = gpos.find((g) => g.gpo_no === gpoNo) || { gpo_no: gpoNo, estimate_no: snapshot?.estimate_no, style_no: snapshot?.style };
    setOpen({ gpo, analysis, snapshot });
    setItems(its || []);
    setForm(analysis ? {
      commercial: analysis.commercial || {}, commercial_rating: analysis.commercial_rating || '',
      material: analysis.material || {},
      material_fabric_rating: analysis.material_fabric_rating || analysis.material_rating || '',
      material_trims_rating: analysis.material_trims_rating || analysis.material_rating || '',
      capacity: analysis.capacity || {}, capacity_rating: analysis.capacity_rating || '',
      technical: analysis.technical || {}, technical_rating: analysis.technical_rating || '',
      conditions: analysis.conditions || '',
      owners: analysis.owners || {},
    } : emptyForm());
    // Management's decision form pre-fills with the system's suggestion so an
    // unchanged decision needs no note; only an override does.
    setDecisionForm({
      overall_rating: analysis?.overall_rating || analysis?.overall_rating_auto || '',
      decision: analysis?.decision || analysis?.decision_auto || '',
      conditions: analysis?.conditions || '',
      rating_override_note: analysis?.rating_override_note || '',
      decision_override_note: analysis?.decision_override_note || '',
    });
  };

  const startNew = async () => {
    if (!pickGpo) return;
    await loadAnalysis(pickGpo);
    setPickGpo('');
  };

  // Open a specific RA directly when asked from elsewhere (e.g. the draft GPO's
  // "Fill / View Risk Analysis" link), keyed on gpo_no.
  useEffect(() => {
    if (!openGpoNo) return;
    loadAnalysis(openGpoNo).catch(() => {}).finally(() => onOpened && onOpened());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openGpoNo]);

  // Mutate a nested section field.
  const setSec = (sec, key, val) => setForm((p) => ({ ...p, [sec]: { ...p[sec], [key]: val } }));
  const setTop = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const setOwner = (cat, val) => setForm((p) => ({ ...p, owners: { ...p.owners, [cat]: val } }));

  const status = open?.analysis?.status || 'draft';
  // Only the merchant (merchandiser) and management/admin may fill/edit the RA. Department
  // heads who reach this form get a READ-ONLY view — their workflow is view + sign-off via
  // the Risk Sign-offs screen. Management still decides/overrides below (canDecide).
  const canFill = isApprover || user?.role === 'merchandiser';
  const editable = canFill && ['draft', 'held'].includes(status);      // filler can edit
  const canDecide = isApprover && status === 'pending_approval';        // management can decide
  const readOnly = !editable;

  // Live auto overall rating + suggested decision (Fabric + Trims counted separately).
  const overallAuto = computeOverall([form.commercial_rating, form.material_fabric_rating, form.material_trims_rating, form.capacity_rating, form.technical_rating]);
  const decisionAuto = SUGGESTED_DECISION[overallAuto] || '';
  // Sign-off roster = the 3 mandatory depts (always) + any optional category flagged Med/High.
  const signCats = OWNER_CATS.filter((c) => c.required || isFlagged(form[c.ratingKey]));
  // Dept-locked owner list for a category (Q5): only that department's users.
  const ownersFor = (catKey) => owners.filter((u) => (CAT_BY_KEY[catKey]?.roles || []).includes(u.role));

  const saveDraft = async () => {
    setSaving(true);
    try {
      const r = await api.post('/risk', { gpo_no: open.gpo.gpo_no, stage, ...form });
      setOpen((o) => ({ ...o, analysis: r.data.data }));
      fetchList();
      alert('Risk Analysis saved as draft.');
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    // Every sign-off category (3 mandatory + any flagged) needs a named owner.
    const missing = signCats.filter((c) => !form.owners?.[c.key]);
    if (missing.length) { alert('Assign an owner for every required sign-off (Fabric, Quality, Production) and any flagged risk:\n• ' + missing.map((c) => c.label).join('\n• ')); return; }
    setSaving(true);
    try {
      // Persist latest edits, then submit for approval.
      const saved = await api.post('/risk', { gpo_no: open.gpo.gpo_no, stage, ...form });
      const r = await api.post('/risk/' + saved.data.data.id + '/submit', {});
      setOpen((o) => ({ ...o, analysis: r.data.data }));
      setItems(r.data.items || []);
      fetchList();
      alert('Submitted. Fabric, Quality and Production must each sign off before it can be approved and the GPO activated.');
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const decide = async (decision) => {
    const a = open.analysis || {};
    const ratingChanged = !!a.overall_rating_auto && decisionForm.overall_rating && decisionForm.overall_rating !== a.overall_rating_auto;
    const decisionChanged = !!a.decision_auto && decision !== a.decision_auto;
    if (decision !== 'hold' && !decisionForm.overall_rating) { alert('Set the Overall Rating before approving.'); return; }
    if (ratingChanged && !decisionForm.rating_override_note) { alert('You changed the overall rating from the suggested "' + RATING_LABEL[a.overall_rating_auto] + '" — enter a note explaining the override.'); return; }
    if (decisionChanged && !decisionForm.decision_override_note) { alert('You chose a decision other than the suggested "' + (DECISION_LABEL[a.decision_auto] || a.decision_auto) + '" — enter a note explaining the override.'); return; }
    if (decision === 'proceed_with_conditions' && !decisionForm.conditions) { alert('Enter the conditions for "Proceed with Conditions".'); return; }
    setSaving(true);
    try {
      const r = await api.post('/risk/' + open.analysis.id + '/decision', {
        decision,
        overall_rating: decision === 'hold' ? (decisionForm.overall_rating || 'high') : decisionForm.overall_rating,
        conditions: decisionForm.conditions || null,
        rating_override_note: ratingChanged ? decisionForm.rating_override_note : null,
        decision_override_note: decisionChanged ? decisionForm.decision_override_note : null,
      });
      setOpen((o) => ({ ...o, analysis: r.data.data }));
      fetchList();
      alert(decision === 'hold'
        ? 'HOLD — GPO stays in DRAFT and is blocked. FRS/procurement remain locked.'
        : 'Authorized — GPO activated & Risk Analysis approved. FRS/procurement are now unlocked.');
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const printRA = () => {
    const a = open.analysis, s = open.snapshot || (a?.snapshot) || {};
    const yn = (v) => v === 'Y' ? 'Yes' : v === 'N' ? 'No' : '—';
    printDocument({
      docTitle: 'Order Execution — Risk Analysis',
      subtitle: `GPO ${s.gpo_no || open.gpo.gpo_no || ''} · ${s.estimate_no || ''} · ${s.style || ''}`,
      meta: [
        ['Status', (STATUS_META[a?.status] || {}).label || '—'],
        ['Decision', DECISION_LABEL[a?.decision] || '—'],
        ['Overall Rating', RATING_LABEL[a?.overall_rating] || '—'],
      ],
      sections: [
        { title: '1 · Order Snapshot', fields: [
          ['Buyer', s.buyer], ['Style', s.style], ['Description', s.garment_desc],
          ['Order Qty', s.order_qty], ['Target Ship Date', fmtDate(s.target_ship_date)],
          ['Costed Price', s.costed_price != null ? '₹ ' + s.costed_price : null],
          ['Margin', s.margin_pct != null ? s.margin_pct + ' %' : null],
        ] },
        { title: `2 · Commercial Risk  [${RATING_LABEL[a?.commercial_rating] || '—'}]`, fields: [
          ['Costing confirmed & margin acceptable', yn(a?.commercial?.costing_confirmed)],
          ['— note', a?.commercial?.costing_note],
          ['Payment terms agreed', yn(a?.commercial?.payment_terms_agreed)],
          ['— note', a?.commercial?.payment_terms_note],
          ['Price locked', yn(a?.commercial?.price_locked)],
          ['— note', a?.commercial?.price_locked_note],
        ] },
        { title: `3 · Material / Sourcing Risk  [Fabric ${RATING_LABEL[a?.material_fabric_rating] || '—'} · Trims ${RATING_LABEL[a?.material_trims_rating] || '—'}]`, fields: [
          ['Fabric availability vs ship date', a?.material?.fabric_availability],
          ['Trims / accessories availability', a?.material?.trims_availability],
          ['Long-lead / imported items', a?.material?.long_lead_items],
          ['Yarn / greige status', a?.material?.yarn_greige_status],
        ] },
        { title: `4 · Capacity / Delivery Risk  [${RATING_LABEL[a?.capacity_rating] || '—'}]`, fields: [
          ['Machine / line capacity', a?.capacity?.line_capacity],
          ['Ship-date realism', a?.capacity?.ship_date_realism],
          ['Clashes with running orders', a?.capacity?.clashes],
        ] },
        { title: `5 · Technical / Quality Risk  [${RATING_LABEL[a?.technical_rating] || '—'}]`, fields: [
          ['New vs repeat style', a?.technical?.new_or_repeat],
          ['Special processes', a?.technical?.special_processes],
          ['Sample / approval status', a?.technical?.sample_approval_status],
          ['Compliance / test needs', a?.technical?.compliance_test_needs],
        ] },
        { title: '6 · Overall Decision', fields: [
          ['System-suggested Rating', RATING_LABEL[a?.overall_rating_auto]],
          ['System-suggested Decision', DECISION_LABEL[a?.decision_auto]],
          ['Final Overall Rating', RATING_LABEL[a?.overall_rating]],
          ['Final Decision', DECISION_LABEL[a?.decision]],
          ['Rating override note', a?.rating_override_note],
          ['Decision override note', a?.decision_override_note],
          ['Conditions', a?.conditions],
        ] },
        // Flagged risks, their owners, and sign-off status.
        ...(items.length ? [{
          title: `7 · Flagged Risks & Department Sign-off  (${a?.ack_done_count || 0}/${a?.ack_required_count || 0} signed off)`,
          table: {
            columns: ['Risk', 'Rating', 'Owner', 'Sign-off'],
            rows: items.map((it) => [
              CAT_LABEL[it.category] || it.category,
              RATING_LABEL[it.rating] || it.rating || '—',
              it.owner_name ? `${it.owner_name}${it.owner_role ? ' (' + it.owner_role + ')' : ''}` : '—',
              it.acknowledged_at ? `Signed off · ${fmtDateTime(it.acknowledged_at)}` : 'Pending',
            ]),
          },
        }] : []),
        { title: 'Authorisation', fields: [
          ['Submitted by', a?.submitted_by_name], ['Submitted on', fmtDateTime(a?.submitted_at)],
          ['Approved by', a?.approved_by_name], ['Approved on', fmtDateTime(a?.approved_at)],
        ] },
        { fields: [
          ['Prepared / Submitted (sign)', '\n\n_______________________'],
          ['Approved — Management (sign)', '\n\n_______________________'],
        ] },
      ],
    });
  };

  // ─── LIST VIEW ──────────────────────────────────────────────────────────────
  if (!open) {
    return (
      <div>
        <div style={{ ...cardStyle, background: VIOLET, border: 'none', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, color: NAVY, fontSize: 13 }}>🛡️ Start a Risk Analysis (on a draft GPO):</div>
          <select value={pickGpo} onChange={(e) => setPickGpo(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: 280, background: '#fff' }}>
            <option value="">Select a GPO…</option>
            {startable.map((g) => <option key={g.gpo_no} value={g.gpo_no}>GPO {g.gpo_no}{g.status === 'draft' ? ' (DRAFT)' : ''} — {g.style_no} {g.brand ? '(' + g.brand + ')' : ''}</option>)}
          </select>
          <button type="button" onClick={startNew} disabled={!pickGpo}
            style={{ padding: '8px 18px', background: pickGpo ? INK : '#bbb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: pickGpo ? 'pointer' : 'default' }}>
            Open →
          </button>
          {!startable.length && <span style={{ fontSize: 12, color: '#888' }}>All GPOs already have a Risk Analysis — open one below.</span>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 8px' }}>
          <GpoFilter value={gpoQ} onChange={setGpoQ} placeholder="Filter by GPO / style / buyer…" width={220}
            title='Partial match over GPO, estimate, style, buyer and brand' />
          <FilterCount shown={shown.length} total={list.length} noun="risk analyses" q={gpoQ} />
        </div>
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>
                {['GPO', 'Estimate', 'Style / Buyer', 'Status', 'Decision', 'Overall', 'Updated', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!shown.length && <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>{list.length ? 'No risk analysis matches that filter.' : 'No risk analyses yet — start one above.'}</td></tr>}
              {shown.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: NAVY }}>{r.gpo_no || '—'}</td>
                  <td style={{ padding: '10px 14px', color: '#666' }}>{r.estimate_no || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>{r.style_no || '—'}{r.brand ? <span style={{ color: '#999' }}> · {r.brand}</span> : ''}</td>
                  <td style={{ padding: '10px 14px' }}><Badge status={r.status} /></td>
                  <td style={{ padding: '10px 14px' }}>{DECISION_LABEL[r.decision] || '—'}</td>
                  <td style={{ padding: '10px 14px' }}><RatingPill value={r.overall_rating} /></td>
                  <td style={{ padding: '10px 14px', color: '#888', fontSize: 12 }}>{fmtDate(r.updated_at)}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <button type="button" onClick={() => loadAnalysis(r.gpo_no)}
                      style={{ padding: '5px 14px', background: VIOLET, color: INK, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      {canDecideRow(r, isApprover) ? 'Review →' : 'Open →'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ListFooter shown={list.length} total={list.length} noun="risk analyses" />
        </div>
      </div>
    );
  }

  // ─── DETAIL / FORM VIEW ───────────────────────────────────────────────────────
  const s = open.snapshot || {};
  const a = open.analysis;
  return (
    <div>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <button type="button" onClick={() => { setOpen(null); fetchList(); }}
          style={{ padding: '6px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>← Back</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Badge status={status} />
          {a?.decision && <span style={{ fontSize: 13, fontWeight: 700, color: a.decision === 'hold' ? RED : '#0F7B3F' }}>{DECISION_LABEL[a.decision]}</span>}
          {a && <button type="button" onClick={printRA} style={printBtnStyle}>🖨️ Print / PDF</button>}
        </div>
      </div>

      {/* Workflow status strip */}
      <StatusStrip status={status} />

      {/* SECTION 1 — Order Snapshot (read-only, auto-filled) */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>1 · Order Snapshot <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#aaa', fontSize: 11 }}>— auto-filled, read-only</span></h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14 }}>
          {[
            ['Buyer', s.buyer], ['Style', s.style], ['Description', s.garment_desc],
            ['Order Qty', s.order_qty], ['Target Ship Date', fmtDate(s.target_ship_date)],
            ['Costed Price', s.costed_price != null ? '₹ ' + s.costed_price : '—'],
            ['Margin', s.margin_pct != null ? s.margin_pct + ' %' : '—'],
          ].map(([k, v]) => (
            <div key={k} style={{ background: WARM, borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>{k}</div>
              <div style={{ fontSize: 14, color: NAVY, fontWeight: 600, marginTop: 2 }}>{v || '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 2 — Commercial Risk */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>2 · Commercial Risk</h3>
        <div style={{ display: 'grid', gap: 14 }}>
          {[
            ['Costing confirmed & margin acceptable', 'costing_confirmed', 'costing_note'],
            ['Payment terms agreed', 'payment_terms_agreed', 'payment_terms_note'],
            ['Price locked', 'price_locked', 'price_locked_note'],
          ].map(([lbl, yk, nk]) => (
            <div key={yk} style={{ display: 'grid', gridTemplateColumns: '1.4fr 130px 1.6fr', gap: 12, alignItems: 'end' }}>
              <div style={{ fontSize: 13, color: '#444', fontWeight: 600 }}>{lbl}</div>
              <YesNo value={form.commercial[yk]} disabled={readOnly} onChange={(v) => setSec('commercial', yk, v)} />
              <input style={inputStyle} placeholder="Note…" value={form.commercial[nk] || ''} disabled={readOnly} onChange={(e) => setSec('commercial', nk, e.target.value)} />
            </div>
          ))}
          <div style={{ maxWidth: 300 }}><RatingSelect value={form.commercial_rating} disabled={readOnly} onChange={(v) => setTop('commercial_rating', v)} /></div>
          {isFlagged(form.commercial_rating) && (
            <OwnerPicker label="Commercial" dept="Commercial / Accounts" users={ownersFor('commercial')} value={form.owners?.commercial} disabled={readOnly} onChange={(v) => setOwner('commercial', v)} />
          )}
        </div>
      </div>

      {/* SECTION 3 — Material / Sourcing Risk */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>3 · Material / Sourcing Risk</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Fabric availability vs ship date">
            <ChipSelect options={['OK', 'Tight', 'At-Risk']} value={form.material.fabric_availability} disabled={readOnly} onChange={(v) => setSec('material', 'fabric_availability', v)} />
          </Field>
          <Field label="Trims / accessories availability">
            <ChipSelect options={['OK', 'Tight', 'At-Risk']} value={form.material.trims_availability} disabled={readOnly} onChange={(v) => setSec('material', 'trims_availability', v)} />
          </Field>
          <Field label="Long-lead / imported items">
            <input style={inputStyle} value={form.material.long_lead_items || ''} disabled={readOnly} onChange={(e) => setSec('material', 'long_lead_items', e.target.value)} />
          </Field>
          <Field label="Yarn / greige status">
            <input style={inputStyle} value={form.material.yarn_greige_status || ''} disabled={readOnly} onChange={(e) => setSec('material', 'yarn_greige_status', e.target.value)} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 24, marginTop: 14, flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 300 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 4 }}>Fabric rating</div>
            <RatingSelect value={form.material_fabric_rating} disabled={readOnly} onChange={(v) => setTop('material_fabric_rating', v)} />
          </div>
          <div style={{ maxWidth: 300 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 4 }}>Trims rating</div>
            <RatingSelect value={form.material_trims_rating} disabled={readOnly} onChange={(v) => setTop('material_trims_rating', v)} />
          </div>
        </div>
        {/* Fabric is a MANDATORY signatory — its picker always shows (even Low). Trims only when flagged. */}
        <OwnerPicker label="Material — Fabric" dept="Fabric" required users={ownersFor('material_fabric')} value={form.owners?.material_fabric} disabled={readOnly} onChange={(v) => setOwner('material_fabric', v)} />
        {isFlagged(form.material_trims_rating) && (
          <OwnerPicker label="Material — Trims" dept="Trims" users={ownersFor('material_trims')} value={form.owners?.material_trims} disabled={readOnly} onChange={(v) => setOwner('material_trims', v)} />
        )}
      </div>

      {/* SECTION 4 — Capacity / Delivery Risk */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>4 · Capacity / Delivery Risk</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Machine / line capacity">
            <ChipSelect options={['Available', 'Tight', 'No']} value={form.capacity.line_capacity} disabled={readOnly} onChange={(v) => setSec('capacity', 'line_capacity', v)} />
          </Field>
          <Field label="Ship-date realism">
            <ChipSelect options={['Comfortable', 'Tight', 'At-Risk']} value={form.capacity.ship_date_realism} disabled={readOnly} onChange={(v) => setSec('capacity', 'ship_date_realism', v)} />
          </Field>
          <Field label="Clashes with running orders">
            <input style={{ ...inputStyle, gridColumn: '1/-1' }} value={form.capacity.clashes || ''} disabled={readOnly} onChange={(e) => setSec('capacity', 'clashes', e.target.value)} />
          </Field>
        </div>
        <div style={{ maxWidth: 300, marginTop: 14 }}><RatingSelect value={form.capacity_rating} disabled={readOnly} onChange={(v) => setTop('capacity_rating', v)} /></div>
        {/* Production is a MANDATORY signatory — its picker always shows. */}
        <OwnerPicker label="Capacity / Delivery" dept="Production / PPC" required users={ownersFor('capacity')} value={form.owners?.capacity} disabled={readOnly} onChange={(v) => setOwner('capacity', v)} />
      </div>

      {/* SECTION 5 — Technical / Quality Risk */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>5 · Technical / Quality Risk</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="New vs repeat style">
            <ChipSelect options={['New', 'Repeat']} value={form.technical.new_or_repeat} disabled={readOnly} onChange={(v) => setSec('technical', 'new_or_repeat', v)} />
          </Field>
          <Field label="Special processes">
            <input style={inputStyle} value={form.technical.special_processes || ''} disabled={readOnly} onChange={(e) => setSec('technical', 'special_processes', e.target.value)} />
          </Field>
          <Field label="Sample / approval status">
            <input style={inputStyle} value={form.technical.sample_approval_status || ''} disabled={readOnly} onChange={(e) => setSec('technical', 'sample_approval_status', e.target.value)} />
          </Field>
          <Field label="Compliance / test needs">
            <input style={inputStyle} value={form.technical.compliance_test_needs || ''} disabled={readOnly} onChange={(e) => setSec('technical', 'compliance_test_needs', e.target.value)} />
          </Field>
        </div>
        <div style={{ maxWidth: 300, marginTop: 14 }}><RatingSelect value={form.technical_rating} disabled={readOnly} onChange={(v) => setTop('technical_rating', v)} /></div>
        {/* Quality is a MANDATORY signatory — its picker always shows. */}
        <OwnerPicker label="Technical / Quality" dept="Quality" required users={ownersFor('technical')} value={form.owners?.technical} disabled={readOnly} onChange={(v) => setOwner('technical', v)} />
      </div>

      {/* SECTION 6 — Overall / Decision */}
      <div style={{ ...cardStyle, borderColor: VIOLET, background: '#fcfcff' }}>
        <h3 style={sectionTitleStyle}>6 · Overall Decision</h3>

        {/* Auto-calculated overall rating + suggested decision (live as ratings change). */}
        <div style={{ background: VIOLET, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: INK, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontWeight: 700 }}>🧮 Auto-calculated</span>
          <span>Overall Rating: {overallAuto ? <RatingPill value={overallAuto} /> : <span style={{ color: '#999' }}>—</span>}</span>
          <span>Suggested Decision: <b>{DECISION_LABEL[decisionAuto] || '—'}</b></span>
          <span style={{ color: '#888', fontSize: 11 }}>(highest-wins of the five section ratings)</span>
        </div>

        {canDecide ? (() => {
          const ratingChanged = !!a?.overall_rating_auto && decisionForm.overall_rating && decisionForm.overall_rating !== a.overall_rating_auto;
          return (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ maxWidth: 320 }}>
              <label style={labelStyle}>Overall Rating <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#888' }}>— suggested {RATING_LABEL[a?.overall_rating_auto] || '—'}</span></label>
              <RatingSelectInline value={decisionForm.overall_rating} onChange={(v) => setDecisionForm((p) => ({ ...p, overall_rating: v }))} />
            </div>
            {ratingChanged && (
              <div>
                <label style={{ ...labelStyle, color: RED }}>Reason for overriding the suggested rating (required)</label>
                <textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }} value={decisionForm.rating_override_note} onChange={(e) => setDecisionForm((p) => ({ ...p, rating_override_note: e.target.value }))} placeholder="Why is the overall rating different from the auto-calculated value?" />
              </div>
            )}
            <div>
              <label style={labelStyle}>Conditions (required for "Proceed with Conditions")</label>
              <textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }} value={decisionForm.conditions} onChange={(e) => setDecisionForm((p) => ({ ...p, conditions: e.target.value }))} placeholder="e.g. proceed once fabric PO confirmed by 20 Jun" />
            </div>
            <div>
              <label style={labelStyle}>Decision override note <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#888' }}>— required only if you choose a decision other than the suggested "{DECISION_LABEL[a?.decision_auto] || '—'}"</span></label>
              <textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }} value={decisionForm.decision_override_note} onChange={(e) => setDecisionForm((p) => ({ ...p, decision_override_note: e.target.value }))} placeholder="Why override the suggested decision?" />
            </div>
            <div style={{ fontSize: 12, color: INK, fontWeight: 600, background: VIOLET, borderRadius: 6, padding: '8px 12px' }}>
              ⚖️ One step: this decision both <b>authorizes the GPO</b> (draft → active) and <b>records the Risk Analysis decision</b>.
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" disabled={saving} onClick={() => decide('proceed')}
                style={{ padding: '10px 20px', background: '#0F7B3F', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', outline: a?.decision_auto === 'proceed' ? '3px solid #B8E6C8' : 'none' }}>✓ Authorize GPO &amp; Proceed</button>
              <button type="button" disabled={saving} onClick={() => decide('proceed_with_conditions')}
                style={{ padding: '10px 20px', background: '#854F0B', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', outline: a?.decision_auto === 'proceed_with_conditions' ? '3px solid #F3E2B8' : 'none' }}>✓ Authorize with Conditions</button>
              <button type="button" disabled={saving} onClick={() => decide('hold')}
                style={{ padding: '10px 20px', background: RED, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', outline: a?.decision_auto === 'hold' ? '3px solid #F5C2C2' : 'none' }}>⛔ Hold — Keep GPO Draft</button>
            </div>
          </div>
          );
        })() : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14 }}>
            {[
              ['Overall Rating', a?.overall_rating ? <RatingPill value={a.overall_rating} /> : '—'],
              ['Decision', DECISION_LABEL[a?.decision] || '—'],
              ['Approver', a?.approved_by_name || '—'],
              ['Decision Date', fmtDateTime(a?.approved_at)],
            ].map(([k, v]) => (
              <div key={k} style={{ background: WARM, borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>{k}</div>
                <div style={{ fontSize: 14, color: NAVY, fontWeight: 600, marginTop: 2 }}>{v}</div>
              </div>
            ))}
            {a?.conditions && <div style={{ gridColumn: '1/-1', background: '#FFF8E8', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#854F0B' }}><b>Conditions:</b> {a.conditions}</div>}
            {a?.rating_override_note && <div style={{ gridColumn: '1/-1', background: '#FBE9E9', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: RED }}><b>Rating override:</b> {a.rating_override_note} <span style={{ color: '#888' }}>(suggested {RATING_LABEL[a.overall_rating_auto] || '—'})</span></div>}
            {a?.decision_override_note && <div style={{ gridColumn: '1/-1', background: '#FBE9E9', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: RED }}><b>Decision override:</b> {a.decision_override_note} <span style={{ color: '#888' }}>(suggested {DECISION_LABEL[a.decision_auto] || '—'})</span></div>}
            {status === 'pending_approval' && !isApprover && <div style={{ gridColumn: '1/-1', color: '#854F0B', fontSize: 13 }}>⏳ Awaiting management approval.</div>}
          </div>
        )}
      </div>

      {/* SECTION 7 — Flagged Risks & Department Sign-off */}
      {items.length > 0 && (
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>7 · Flagged Risks & Department Sign-off
            <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#888', fontSize: 12, marginLeft: 8 }}>
              {(a?.ack_done_count || 0)}/{(a?.ack_required_count || 0)} heads signed off
            </span>
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>
              {['Risk', 'Rating', 'Owner (Dept Head)', 'Mapped Documents', 'Sign-off'].map((h) => (
                <th key={h} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600, color: NAVY }}>{CAT_LABEL[it.category] || it.category}</td>
                  <td style={{ padding: '8px 12px' }}><RatingPill value={it.rating} /></td>
                  <td style={{ padding: '8px 12px' }}>{it.owner_name || '—'}{it.owner_role ? <span style={{ color: '#999' }}> · {it.owner_role}</span> : ''}</td>
                  <td style={{ padding: '8px 12px', color: '#888', fontSize: 12 }}>{(Array.isArray(it.mapped_documents) ? it.mapped_documents : []).join(', ') || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {it.acknowledged_at
                      ? <span style={{ color: '#0F7B3F', fontWeight: 700 }}>✓ Signed off<span style={{ color: '#888', fontWeight: 400 }}> · {fmtDate(it.acknowledged_at)}</span></span>
                      : <span style={{ color: '#854F0B', fontWeight: 700 }}>⏳ Pending</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Department sign-off runs in parallel with management approval; it does not gate the GPO.</div>
        </div>
      )}

      {/* Action bar (filler) */}
      {editable && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8, position: 'sticky', bottom: 0, background: '#fff', padding: '12px 0', borderTop: '1px solid #eee' }}>
          <button type="button" disabled={saving} onClick={saveDraft}
            style={{ padding: '10px 20px', background: '#fff', color: INK, border: `1px solid ${INK}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save Draft'}</button>
          <button type="button" disabled={saving} onClick={submit}
            style={{ padding: '10px 20px', background: INK, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{saving ? '…' : 'Submit for Approval →'}</button>
        </div>
      )}

      {a && (a.submitted_by_name || a.approved_by_name) && (
        <div style={{ fontSize: 12, color: '#999', marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {a.submitted_by_name && <span>Submitted by <b>{a.submitted_by_name}</b> · {fmtDateTime(a.submitted_at)}</span>}
          {a.approved_by_name && <span>Decided by <b>{a.approved_by_name}</b> · {fmtDateTime(a.approved_at)}</span>}
        </div>
      )}
    </div>
  );
}

// Helper: would this list row open in "review" mode for the current user?
function canDecideRow(r, isApprover) {
  return isApprover && r.status === 'pending_approval';
}

// Inline rating selector reused by the management decision block.
function RatingSelectInline({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {RATINGS.map((r) => {
        const on = value === r;
        const c = TONE[ratingTone(r)];
        return (
          <button key={r} type="button" onClick={() => onChange(r)}
            style={{ flex: 1, padding: '8px 0', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              border: on ? `1px solid ${c}` : '1px solid #ddd', background: on ? c + '1A' : '#fff', color: on ? c : '#999' }}>
            {RATING_LABEL[r]}
          </button>
        );
      })}
    </div>
  );
}

// Draft → Pending → Approved/Held progress strip.
function StatusStrip({ status }) {
  const held = status === 'held';
  const steps = [
    { key: 'draft', label: 'Draft' },
    { key: 'pending_approval', label: 'Pending Approval' },
    { key: held ? 'held' : 'approved', label: held ? 'Held' : 'Approved' },
  ];
  const order = { draft: 0, pending_approval: 1, approved: 2, held: 2 };
  const cur = order[status] ?? 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
      {steps.map((st, i) => {
        const done = i < cur, active = i === cur;
        const isEnd = i === 2;
        const c = active ? (held ? RED : (isEnd ? '#0F7B3F' : INK)) : (done ? INK : '#ccc');
        return (
          <React.Fragment key={st.key}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: (active || done) ? c : '#eee', color: (active || done) ? '#fff' : '#999', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
              <span style={{ fontSize: 12, fontWeight: active ? 700 : 600, color: c }}>{st.label}</span>
            </div>
            {i < steps.length - 1 && <div style={{ flex: 'none', width: 28, height: 2, background: done ? INK : '#eee' }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
